import { MouseEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingService, BuildingOrg, Contract, ContractServices } from 'shared/models';
import { propertyNameLabelKeys } from '../orgRoles';
import { getExpiryMessageHtml, BillingServiceName } from 'ui/contracts/contracts';
import { sortNamesBy } from 'logic/sortNames';

require('./BuildingOrgListItem.css');

export type Props = {
    org: BuildingOrg;
    contract?: Contract;
    unbilledServices: readonly BillingService[];
    overbilledServices: readonly BillingService[];
    onClickEdit?: MouseEventHandler<HTMLElement>;
};

export const BuildingOrgListItem = ({ org, contract, unbilledServices, overbilledServices, onClickEdit }: Props) => {
    const { t } = useTranslation();
    const { orgId, displayName, properties } = org;

    const typeString =
        (Object.keys(properties) as (keyof BuildingOrg['properties'])[])
            .filter(n => !!properties[n])
            .map(n => t(propertyNameLabelKeys[n]))
            .join(', ');

    return (
        <tr
            id={ `boli-${orgId}` }
            className="building-org-list-item"
        >
            <td>
                <div>
                    { displayName }
                </div>
                <div className="_type">
                    { typeString }
                </div>
            </td>
            <td>
                { contract &&
                    <>
                        <div>
                            <span className={ `services ${contract.locked ? 'locked' : ''}` }>
                                { contract.locked &&
                                    <i
                                        className="grey lock icon"
                                        title="Locked"
                                    ></i>
                                }
                                { makeContractServicesHtml(contract.services, unbilledServices, overbilledServices) }
                            </span>
                        </div>
                        <div className="_type">
                            { getExpiryMessageHtml(contract.expiryDate, false) }
                        </div>
                    </>
                }
            </td>
            <td>
                { onClickEdit &&
                    <button
                        type="button"
                        className="ui tiny basic icon button"
                        data-id={ orgId }
                        onClick={ onClickEdit }
                    >
                        <i className="setting icon"/>
                    </button>
                }
            </td>
        </tr>
    );
};

export function makeContractServicesHtml(services: ContractServices, unbilledServices: readonly BillingService[], overbilledServices: readonly BillingService[]) {
    const nodes: ReactNode[] = [];

    for (const key of Object.keys(services).sort(sortNamesBy(key => BillingServiceName[key as BillingService]))) {
        const billingService = key as BillingService;

        if (nodes.length) {
            nodes.push(
                <span
                    className="_separator"
                    key={ nodes.length }
                >
                    { ',' }
                </span>
            );
        }

        const className =
            unbilledServices.includes(billingService)
                ? '_unbilled'
                : overbilledServices.includes(billingService)
                    ? '_overbilled'
                    : undefined;

        nodes.push(
            <span className={ className } key={ nodes.length }>
                { BillingServiceName[billingService] }
            </span>
        );
    }

    return nodes;
}
